import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
} from "@mui/material";
import { useEffect } from "react";
import FormTextField from "../../../components/elements/FormTextField";
import {
  CATEGORIES_LIST,
  ModuleCategoryEnum,
  ModuleModel,
} from "../../../data/models/modules/ModuleModel";
import { useFormWithNavigation } from "../../hooks/useFormWithNavigation";

interface Props {
  open: boolean;
  onClose: () => void;
  initialValue?: ModuleModel;
  onSave: (
    data: Omit<ModuleModel, "id"> | Partial<Omit<ModuleModel, "id">>
  ) => Promise<void> | void;
  loading: boolean;
}

export default function CreateEditModuleDialog({
  open,
  onClose,
  initialValue,
  onSave,
  loading,
}: Props) {
  const initial: Partial<ModuleModel> = {
    name: "",
    category: ModuleCategoryEnum.TABLE,
  };

  const validate = (
    field: keyof typeof initial,
    value: any,
    form: typeof initial
  ) => {
    if (field === "name" && !String(value).trim())
      return "El nombre es obligatorio";
    if (field === "category" && !value) return "Seleccione una categoría";
    return "";
  };

  const {
    form,
    setForm,
    errors,
    refs,
    handleChange,
    handleEnter,
    validateAll,
  } = useFormWithNavigation(initial, validate, { autofocus: true });

  useEffect(() => {
    if (open && !initialValue) {
      setForm(initial);
      setTimeout(() => {
        const firstKey = Object.keys(refs)[0] as keyof typeof initial;
        refs[firstKey]?.current?.focus();
      }, 50);
    }
  }, [open, initialValue]);

  useEffect(() => {
    if (open && initialValue) {
      const { id, ...rest } = initialValue;
      setForm(rest);
    }
  }, [open, initialValue]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!validateAll()) return;
    await onSave(form);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {initialValue ? "Editar módulo" : "Nuevo módulo"}
      </DialogTitle>

      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Grid container spacing={2} direction="column">
            <Grid item xs={12}>
              <FormTextField
                label="Nombre"
                field="name"
                form={{ ...form, name: form.name }}
                errors={errors}
                refs={refs}
                onChange={handleChange("name", (v) => v.toUpperCase())}
                onKeyDown={handleEnter("name")}
              />
            </Grid>

            <Grid item xs={12}>
              <FormTextField
                label="Categoría"
                field="category"
                select
                form={form}
                errors={errors}
                refs={refs}
                onChange={handleChange("category")}
                onKeyDown={handleEnter("category")}
              >
                {CATEGORIES_LIST.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </FormTextField>
            </Grid>
          </Grid>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button variant="contained" type="submit" loading={loading}>
            {initialValue ? "Guardar cambios" : "Crear módulo"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
